import React, { useContext, useEffect, useReducer } from 'react';
import axios from 'axios';
import { Store } from '../Store';
import { getError } from '../utils';
import Container from 'react-bootstrap/Container';
import Table from 'react-bootstrap/Table';
import Button from 'react-bootstrap/Button';
import { Helmet } from 'react-helmet-async';
import LoadingBox from '../components/LoadingBox';
import MessageBox from '../components/MessageBox';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import Chart from 'react-google-charts';

const reducer = (state, action) => {
    switch (action.type) {
        case 'FETCH_REQUEST':
            return { ...state, loading: true };
        case 'FETCH_SUCCESS':
            return { ...state, loading: false, stats: action.payload };
        case 'FETCH_FAIL':
            return { ...state, loading: false, error: action.payload };
        default:
            return state;
    }
};

export default function ManagerReportScreen() {
    const { state } = useContext(Store);
    const { userInfo } = state;
    const [{ loading, error, stats }, dispatch] = useReducer(reducer, {
        loading: true,
        error: '',
        stats: [],
    });

    useEffect(() => {
        const fetchData = async () => {
            try {
                dispatch({ type: 'FETCH_REQUEST' });
                const { data } = await axios.get('/api/orders/manager/report', {
                    headers: { Authorization: `Bearer ${userInfo.token}` },
                });
                dispatch({ type: 'FETCH_SUCCESS', payload: data });
            } catch (err) {
                dispatch({
                    type: 'FETCH_FAIL',
                    payload: getError(err),
                });
            }
        };
        fetchData();
    }, [userInfo]);

    return (
        <Container fluid className="py-4">
            <Helmet>
                <title>Product Sales Report</title>
            </Helmet>
            <h1 className="mb-4">Detailed Product Report</h1>
            <p className="text-muted mb-4">Performance statistics for your menu items.</p>

            {loading ? (
                <LoadingBox />
            ) : error ? (
                <MessageBox variant="danger">{error}</MessageBox>
            ) : (
                <Row>
                    <Col lg={8}>
                        <Card className="mb-4 shadow-sm">
                            <Card.Body>
                                <Card.Title>Sales by Product</Card.Title>
                                <div className="table-responsive">
                                    <Table hover>
                                        <thead className="bg-light">
                                            <tr>
                                                <th>Image</th>
                                                <th>Product Name</th>
                                                <th className="text-end">Quantity Sold</th>
                                                <th className="text-end">Total Revenue</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {stats.map((item) => (
                                                <tr key={item._id}>
                                                    <td>
                                                        <img
                                                            src={item.image}
                                                            alt={item.name}
                                                            className="img-fluid rounded"
                                                            style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                                                        />
                                                    </td>
                                                    <td className="align-middle fw-bold">{item.name}</td>
                                                    <td className="align-middle text-end">{item.quantitySold}</td>
                                                    <td className="align-middle text-end">{item.totalSales.toLocaleString()} RWF</td>
                                                </tr>
                                            ))}
                                            {stats.length === 0 && (
                                                <tr>
                                                    <td colSpan="4" className="text-center py-4 text-muted">
                                                        No sales data available yet.
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                        {stats.length > 0 && (
                                            <tfoot className="bg-light fw-bold">
                                                <tr>
                                                    <td colSpan="2">TOTAL</td>
                                                    <td className="text-end">
                                                        {stats.reduce((acc, curr) => acc + curr.quantitySold, 0)}
                                                    </td>
                                                    <td className="text-end">
                                                        {stats.reduce((acc, curr) => acc + curr.totalSales, 0).toLocaleString()} RWF
                                                    </td>
                                                </tr>
                                            </tfoot>
                                        )}
                                    </Table>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col lg={4}>
                        <Card className="mb-4 shadow-sm">
                            <Card.Body>
                                <Card.Title>Top Performers</Card.Title>
                                {stats.length > 0 ? (
                                    <Chart
                                        width={'100%'}
                                        height={'300px'}
                                        chartType="PieChart"
                                        loader={<div>Loading Chart...</div>}
                                        data={[
                                            ['Product', 'Sales'],
                                            ...stats.map((x) => [x.name, x.totalSales]),
                                        ]}
                                        options={{
                                            title: 'Revenue Share',
                                            is3D: true,
                                            chartArea: { width: '90%', height: '80%' },
                                            legend: { position: 'bottom' }
                                        }}
                                    />
                                ) : (
                                    <p className="text-center text-muted py-5">No data for charts</p>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}
        </Container>
    );
}
